<template>
  <div class="dv-toolbar flex items-center gap-3 mb-4 flex-nowrap" :class="[centerNav ? 'justify-center' : '', lockedTeacher ? 'dv-no-actions-menu' : '']">
    <!-- Standard calendar nav (Google/Outlook pattern): Today, prev/next arrows grouped tight, then
         the clickable date label, all left-aligned. flex-1 absorbs the leftover space so the
         button cluster lands at the right edge. centerNav (App.vue's locked-teacher mobile
         view) keeps whole-row centering, since there's no button cluster of note there. -->
    <div class="flex items-center gap-3" :class="centerNav ? '' : 'flex-1'">
      <button @click="$emit('today')" class="h-7 flex items-center justify-center px-3 text-xs rounded-md border border-stroke-strong dark:border-stroke-strong-dark hover:bg-surface-muted dark:hover:bg-surface-hover-dark transition-colors text-fg-secondary dark:text-fg-secondary-dark flex-shrink-0">
        Today
      </button>
      <div class="flex items-center">
        <button @click="$emit('prev')" class="h-7 w-7 flex items-center justify-center rounded-md text-fg-secondary dark:text-fg-muted-dark hover:bg-surface-muted dark:hover:bg-surface-hover-dark transition-colors" title="Previous">
          <ChevronLeftIcon class="h-4 w-4" />
        </button>
        <button @click="$emit('next')" class="h-7 w-7 flex items-center justify-center rounded-md text-fg-secondary dark:text-fg-muted-dark hover:bg-surface-muted dark:hover:bg-surface-hover-dark transition-colors" title="Next">
          <ChevronRightIcon class="h-4 w-4" />
        </button>
      </div>
      <DateJumpInput
        class="dv-date-label group inline-flex items-center gap-2 text-sm font-semibold text-fg-base dark:text-fg-base-dark select-none hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
        :class="{ 'dv-date-keep-long': keepLongDate }"
        :model-value="dateIso" @update:model-value="$emit('jump-date', $event)"
      >
        <CalendarDaysIcon class="h-4 w-4 flex-shrink-0 text-fg-muted dark:text-fg-muted-dark group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
        <template v-if="dateLabelShort">
          <span class="dv-date-long">{{ dateLabelLong }}</span>
          <span class="dv-date-short hidden">{{ dateLabelShort }}</span>
        </template>
        <template v-else>{{ dateLabelLong }}</template>
      </DateJumpInput>
      <CalendarTabSwitcher v-if="!hideTabSwitcher" class="sm:hidden ml-1" />
    </div>

    <!-- Everything after the date nav — mode picker, filters, actions — is one group.
         No margin needed since the date-nav div above is flex-1 and already absorbs the
         leftover space, landing this group at the right edge. -->
    <div class="flex items-center gap-2">
      <slot name="extra-controls" />

      <CalendarFilterBar
        v-if="!lockedTeacher"
        :show-teachers="true"
        :all-teachers="allTeachers"
        :all-instruments="allInstruments"
        :all-locations="allLocations"
        :active-teachers="activeTeachers"
        :selected-location="selectedLocation"
        :is-dark="isDark"
        :is-instrument-active="isInstrumentActive"
        :is-instrument-partial="isInstrumentPartial"
        @toggle-teacher="$emit('toggle-teacher', $event)"
        @deselect-all="$emit('deselect-all')"
        @toggle-instrument="$emit('toggle-instrument', $event)"
        @select-location="$emit('select-location', $event)"
      />

      <!-- Refresh/+New Lesson — the far-right, last-in-row group. Two
           representations, mutually exclusive via .dv-toolbar's container query:
           individual buttons on wide toolbars, or ... on narrow ones. -->
      <div class="dv-actions-separate flex items-center gap-3">
        <button @click="$emit('refresh')" :disabled="isLoading" class="dv-refresh-btn h-7 w-7 flex items-center justify-center rounded-md text-fg-secondary dark:text-fg-muted-dark hover:bg-surface-muted dark:hover:bg-surface-hover-dark transition-colors disabled:opacity-40" title="Refresh">
          <ArrowPathIcon class="h-4 w-4" :class="isLoading && 'animate-spin'" />
        </button>
        <button v-if="!readonly" @click="$emit('new-lesson')"
          class="dv-new-lesson-btn h-7 flex items-center justify-center px-3 text-xs rounded-md border border-stroke-strong dark:border-stroke-strong-dark hover:bg-surface-muted dark:hover:bg-surface-hover-dark transition-colors text-fg-secondary dark:text-fg-secondary-dark whitespace-nowrap flex-shrink-0">
          + New Lesson
        </button>
      </div>

      <FilterIconDropdown class="dv-actions-combined hidden" :icon="EllipsisHorizontalIcon" label="More actions">
        <template #default="{ close }">
          <button @click="close(); $emit('refresh')" :disabled="isLoading"
            class="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-hover-dark transition-colors disabled:opacity-40">
            <ArrowPathIcon class="h-3.5 w-3.5 flex-shrink-0 text-fg-faint dark:text-fg-faint-dark" :class="isLoading && 'animate-spin'" />
            Refresh
          </button>
          <button v-if="!readonly" @click="close(); $emit('new-lesson')"
            class="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-hover-dark transition-colors">
            <PlusIcon class="h-3.5 w-3.5 flex-shrink-0 text-fg-faint dark:text-fg-faint-dark" />
            New Lesson
          </button>
        </template>
      </FilterIconDropdown>
    </div>
  </div>
</template>

<script setup>
import { ChevronLeftIcon, ChevronRightIcon, ArrowPathIcon, EllipsisHorizontalIcon, CalendarDaysIcon, PlusIcon } from '@heroicons/vue/24/outline'
import CalendarFilterBar from './CalendarFilterBar.vue'
import CalendarTabSwitcher from './CalendarTabSwitcher.vue'
import FilterIconDropdown from './FilterIconDropdown.vue'
import DateJumpInput from './DateJumpInput.vue'

defineProps({
  dateIso:         { type: String, required: true },
  dateLabelLong:   { type: String, required: true },
  dateLabelShort:  { type: String, default: '' },   // omit to never compress (e.g. Month's label is already short)
  keepLongDate:    { type: Boolean, default: false }, // locked-teacher view — no filters competing for room, so keep the long form
  hideTabSwitcher: { type: Boolean, default: false },
  centerNav:       { type: Boolean, default: false },
  lockedTeacher:   { type: String, default: null },
  allTeachers:         { type: Array,   default: () => [] },
  allInstruments:      { type: Array,   default: () => [] },
  allLocations:        { type: Array,   default: () => [] },
  activeTeachers:      { type: Object,  default: () => new Set() },
  selectedLocation:    { type: String,  default: null },
  isDark:              { type: Boolean, default: false },
  isInstrumentActive:  { type: Function, default: () => false },
  isInstrumentPartial: { type: Function, default: () => false },
  isLoading:       { type: Boolean, default: false },
  readonly:        { type: Boolean, default: false },
})

defineEmits([
  'prev', 'next', 'jump-date', 'today', 'refresh', 'new-lesson',
  'toggle-teacher', 'deselect-all', 'toggle-instrument', 'select-location',
])
</script>

<style scoped>
/* Toolbar: consolidate/compress low-priority elements one at a time as the panel
   narrows (e.g. the resizable teacher-dashboard split, or the Team Calendar page's own
   chrome, or a narrow phone screen) instead of wrapping the row to a 2nd line. Order:
   the long date label shortens (its 240px min-width is otherwise the single biggest
   space hog), then Refresh/+New Lesson consolidate into a single icon-only "..."
   menu (.dv-actions-separate/.dv-actions-combined — this replaces them entirely rather
   than hiding them, so the actions stay reachable), then the Day/Week/Month pill
   switcher compresses into an icon-only "view" dropdown (.dv-mode-pills/.dv-mode-select,
   defined by whichever parent supplies the #extra-controls slot), then Teachers/
   Instrument/Location each compress from a labeled dropdown into an icon-only one
   (.dv-filters-separate/.dv-filters-icons, defined in CalendarFilterBar.vue) as the
   last resort. Shared by DayView/WeekView/MonthView so all three toolbars behave and
   compress identically. */
.dv-toolbar {
  container-type: inline-size;
  container-name: dv-toolbar;
}

.dv-date-label { min-width: 240px; }

@container dv-toolbar (max-width: 900px) {
  /* dv-date-keep-long: locked-teacher view — no filters in the toolbar, so the long
     date fits and is more useful than the compressed short form. */
  .dv-date-label:not(.dv-date-keep-long) { min-width: 0 !important; }
  .dv-date-label:not(.dv-date-keep-long) .dv-date-long { display: none !important; }
  .dv-date-label:not(.dv-date-keep-long) .dv-date-short { display: inline !important; }
}

@container dv-toolbar (max-width: 820px) {
  .dv-actions-separate { display: none !important; }
  .dv-actions-combined { display: flex !important; }
}

/* Locked-teacher view (App.vue's individual teacher dashboard) — no "..." fallback here:
   Refresh/+New Lesson show as separate buttons on wide toolbars, and just disappear
   entirely (rather than collapsing into a "..." menu) once the toolbar gets narrow. */
.dv-no-actions-menu .dv-actions-combined { display: none !important; }

@container dv-toolbar (max-width: 820px) {
  .dv-no-actions-menu .dv-actions-separate { display: none !important; }
}
</style>
